// src/app/features/dashboard/widgets/ScarletWell/CheckIn/ScarletWellCheckIn.js
import React from 'react'
import { Box, Button, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import {
  fetchScarletWellCheckInData,
  selectScarletWellData,
  selectScarletWellStatus,
  submitAllAnswers,
} from '../scarletWellSlice'
import BackButtonNavBar from '../../../../../CommonComponents/BackButtonNavBar/BackButtonNavBar'
import Modal from '../../../../../CommonComponents/Modal/Modal'
import Loading from '../../helpers/Loading'
import { ErrorMessage } from '../../../DashboardComponents/ErrorMessages/ErrorMsg'
import useScarletWellCheckInStyles from '../ScarletWellCheckInStyles'
import {
  getSortedResponseOptions,
  getAnswerScaleLayoutStyle,
  getIncompleteCategoryIds,
  handleCloseModal as handleCloseModalHelper,
  handleScrollToTop as handleScrollToTopHelper,
  onPreviousCategory as onPreviousCategoryHelper,
  onNextCategory as onNextCategoryHelper,
} from './checkInHelpers'
import {
  SurveyIntroCard,
  SurveyCategoryPanel,
  SurveyQuestionTable,
  SurveyFooterActions,
} from './checkInComponents'

function renderPageState(
  status,
  surveyData,
  selectedCategoryId,
  selectedAnswers,
  invalidCategoryIds,
  hasAttemptedSubmit,
  isSubmitting,
  classes,
  setSelectedCategoryId,
  setSelectedAnswers,
  onPreviousCategory,
  onNextCategory,
  onSubmit,
) {
  switch (status) {
    case 'fulfilled':
      return (
        <SurveyContent
          surveyData={surveyData}
          selectedCategoryId={selectedCategoryId}
          selectedAnswers={selectedAnswers}
          invalidCategoryIds={invalidCategoryIds}
          hasAttemptedSubmit={hasAttemptedSubmit}
          isSubmitting={isSubmitting}
          classes={classes}
          setSelectedCategoryId={setSelectedCategoryId}
          setSelectedAnswers={setSelectedAnswers}
          onPreviousCategory={onPreviousCategory}
          onNextCategory={onNextCategory}
          onSubmit={onSubmit}
        />
      )
    case 'error':
      return (
        <Box className={classes.stateContainer}>
          <ErrorMessage />
        </Box>
      )
    case 'pending':
    default:
      return (
        <Box className={classes.stateContainer}>
          <Loading />
        </Box>
      )
  }
}

function SurveyContent(props) {
  const {
    surveyData,
    selectedCategoryId,
    selectedAnswers,
    invalidCategoryIds,
    hasAttemptedSubmit,
    isSubmitting,
    classes,
    setSelectedCategoryId,
    setSelectedAnswers,
    onPreviousCategory,
    onNextCategory,
    onSubmit,
  } = props
  const theme = useTheme()
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('sm'))

  // Memoize categories to prevent returning a new array reference on every render
  const categories = React.useMemo(
    () => surveyData?.checkInPage?.categories || [],
    [surveyData?.checkInPage?.categories],
  )

  // Memoize sorted response options to prevent re-sorting on every render
  const responseOptions = React.useMemo(
    () =>
      getSortedResponseOptions(surveyData?.checkInPage?.responseOptions || []),
    [surveyData?.checkInPage?.responseOptions],
  )

  const answerScaleLayoutStyle = React.useMemo(
    () =>
      isCompactScreen ? getAnswerScaleLayoutStyle(responseOptions) : undefined,
    [isCompactScreen, responseOptions],
  )

  const activeCategory = React.useMemo(
    () =>
      categories.find(
        (category) => category.categoryId === selectedCategoryId,
      ) || categories[0],
    [categories, selectedCategoryId],
  )

  const activeCategoryId = activeCategory ? activeCategory.categoryId : null
  const activeCategoryIndex = categories.findIndex(
    (category) => category.categoryId === activeCategoryId,
  )
  const showPreviousButton = activeCategoryIndex > 0
  const showNextButton =
    activeCategoryIndex > -1 && activeCategoryIndex < categories.length - 1

  const answers = React.useMemo(() => {
    if (!responseOptions || responseOptions.length === 0) return ''
    return responseOptions.map((option) => option.responseTitle).join(' - ')
  }, [responseOptions])

  const recentSubmissionDate =
    surveyData?.checkInPage?.redCapSubmissions?.[0]?.date
  const isRecentSubmission = React.useMemo(() => {
    if (!recentSubmissionDate) return false
    const submissionTime = new Date(recentSubmissionDate).getTime()
    const now = new Date().getTime()
    const hoursDifference = (now - submissionTime) / (1000 * 60 * 60)
    return hoursDifference >= 0 && hoursDifference < 24
  }, [recentSubmissionDate])

  return (
    <Box className={classes.surveyShell}>
      {/* Screen Reader Live Region for Announcements */}
      <Box
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {isSubmitting
          ? 'Submitting your wellness inventory, please wait...'
          : ''}
      </Box>

      <SurveyIntroCard answers={answers} classes={classes} />

      <SurveyCategoryPanel
        categories={categories}
        activeCategoryId={activeCategoryId}
        invalidCategoryIds={invalidCategoryIds}
        hasAttemptedSubmit={hasAttemptedSubmit}
        isRecentSubmission={isRecentSubmission}
        setSelectedCategoryId={setSelectedCategoryId}
        classes={classes}
      />

      <SurveyQuestionTable
        activeCategory={activeCategory}
        responseOptions={responseOptions}
        selectedAnswers={selectedAnswers}
        setSelectedAnswers={setSelectedAnswers}
        answerScaleLayoutStyle={answerScaleLayoutStyle}
        classes={classes}
      />

      <SurveyFooterActions
        showPreviousButton={showPreviousButton}
        showNextButton={showNextButton}
        isSubmitting={isSubmitting}
        isRecentSubmission={isRecentSubmission}
        onPreviousCategory={onPreviousCategory}
        onNextCategory={onNextCategory}
        onSubmit={onSubmit}
        classes={classes}
      />
    </Box>
  )
}

function ScarletWellCheckIn() {
  const classes = useScarletWellCheckInStyles()
  const theme = useTheme()
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'))
  const history = useHistory()
  const dispatch = useDispatch()
  const surveyData = useSelector(selectScarletWellData)
  const status = useSelector(selectScarletWellStatus)
  const drawerIsOpen = useSelector((state) => state.navigation.drawerIsOpen)
  const [selectedCategoryId, setSelectedCategoryId] = React.useState(null)
  const [selectedAnswers, setSelectedAnswers] = React.useState({})
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState(false)
  const [needsFocus, setNeedsFocus] = React.useState(false)

  // Memoize categories payload to prevent stable array reference loss
  const categories = React.useMemo(
    () => surveyData?.checkInPage?.categories || [],
    [surveyData?.checkInPage?.categories],
  )

  // Wrap invalid categories calculation in useMemo so it only computes when answers or categories change
  const invalidCategoryIds = React.useMemo(
    () => getIncompleteCategoryIds(categories, selectedAnswers),
    [categories, selectedAnswers],
  )

  const handleCloseModal = React.useCallback(() => {
    handleCloseModalHelper({ setIsModalOpen, history })
  }, [history])

  const handleScrollToTop = React.useCallback(
    (shouldFocus = true) => {
      handleScrollToTopHelper(shouldFocus, {
        categoryPanelClass: classes.categoryPanel,
        isMdDown,
        setNeedsFocus,
      })
    },
    [classes.categoryPanel, isMdDown],
  )

  const onPreviousCategory = React.useCallback(
    (event) => {
      onPreviousCategoryHelper(event, {
        categories,
        setSelectedCategoryId,
        scrollToTop: handleScrollToTop,
      })
    },
    [categories, handleScrollToTop],
  )

  const onNextCategory = React.useCallback(
    (event) => {
      onNextCategoryHelper(event, {
        categories,
        setSelectedCategoryId,
        scrollToTop: handleScrollToTop,
      })
    },
    [categories, handleScrollToTop],
  )

  const handleSubmit = React.useCallback(async () => {
    if (categories.length === 0) {
      return
    }

    setHasAttemptedSubmit(true)

    if (invalidCategoryIds.length > 0) {
      setSelectedCategoryId(invalidCategoryIds[0])

      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
      const scrollBehavior = prefersReducedMotion ? 'auto' : 'smooth'
      window.scrollTo({ top: 0, behavior: scrollBehavior })
    } else {
      setIsSubmitting(true)
      setSubmitError(false)

      const allAnswers = categories.map((category) => ({
        categoryId: category.categoryId,
        responses: category.questions.map((question) => {
          const responseId = selectedAnswers[question.questionId]
          return {
            questionId: question.questionId,
            responseId,
            responseScore: responseId,
          }
        }),
      }))

      try {
        console.log('Submitting answers:', allAnswers)
        // Redux Toolkit's .unwrap() is used to throw rejected thunk actions (like Axios 400 responses) into this catch block.
        await dispatch(submitAllAnswers(allAnswers)).unwrap()
        setSubmitError(false)
      } catch (error) {
        console.error('Error submitting categories:', error)
        setSubmitError(true)
      } finally {
        setIsSubmitting(false)
        setSelectedAnswers({})
        setIsModalOpen(true)
      }
    }
  }, [categories, invalidCategoryIds, selectedAnswers, dispatch])

  // Wait for React to render the new category, then focus if needed
  React.useEffect(() => {
    if (needsFocus) {
      const firstRadioInput = document.querySelector(
        `.${classes.questionTable} input[type="radio"]`,
      )
      if (firstRadioInput) {
        // Use preventScroll: true so we don't jump away from the window scroll
        firstRadioInput.focus({ preventScroll: true })
        setNeedsFocus(false) // Reset flag after focusing
      }
    }
  }, [selectedCategoryId, needsFocus, classes.questionTable])

  React.useEffect(() => {
    const fetchSurveyData = async () => {
      try {
        await Promise.all([dispatch(fetchScarletWellCheckInData())])
      } catch (err) {
        console.log('Error fetching Scarlet Well Data', err)
      }
    }
    fetchSurveyData()
  }, [dispatch])

  React.useEffect(() => {
    if (
      surveyData &&
      surveyData?.checkInPage?.categories &&
      surveyData?.checkInPage?.categories.length > 0 &&
      selectedCategoryId == null
    ) {
      setSelectedCategoryId(surveyData.checkInPage.categories[0].categoryId)
    }
  }, [surveyData, selectedCategoryId])

  // --- Modal Content Views ---
  const submitSuccess = (
    <div className={classes.modalContent}>
      <Typography variant="h4">
        Thank you for completing the ScarletWell Wellness survey.
      </Typography>
      <br />
      <Typography variant="h5">
        View your latest scores on the widget.
      </Typography>
      <Typography variant="h5">
        Don&apos;t forget to check in regularly.
      </Typography>
    </div>
  )

  const submitFail = (
    <div className={classes.modalContent}>
      <Typography variant="h4">
        There was an error submitting your survey.
      </Typography>
      <br />
      <Typography variant="h5">Please try again later.</Typography>
    </div>
  )

  return (
    <Box
      className={
        drawerIsOpen ? classes.centerPageDrawerOpen : classes.centerPage
      }
      id="main-content"
    >
      <Box>
        <BackButtonNavBar
          onClick={() => history.push('/')}
          name="Back"
          topMargin={{ xs: 0, sm: 0, md: 64, lg: 64 }}
        />
      </Box>
      <Box className={classes.pageContainer}>
        {renderPageState(
          status,
          surveyData,
          selectedCategoryId,
          selectedAnswers,
          invalidCategoryIds,
          hasAttemptedSubmit,
          isSubmitting,
          classes,
          setSelectedCategoryId,
          setSelectedAnswers,
          onPreviousCategory,
          onNextCategory,
          handleSubmit,
        )}
      </Box>
      <Modal
        open={isModalOpen}
        onCloseDialog={handleCloseModal}
        dialogTitle={submitError ? 'Submission Failed' : 'Submission Complete'}
        dialogContent={submitError ? submitFail : submitSuccess}
        dialogActions={
          <Button
            color="primary"
            variant="contained"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        }
      />
    </Box>
  )
}

export default ScarletWellCheckIn
